import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import Nav from "../components/Nav";
import { Recruit } from "../lib/api";
import { getSettings, saveSettings, hasKey } from "../lib/settings";
import { toast } from "sonner";
import { Eye, EyeOff, Sparkles, ExternalLink, Check, Info, Shield, ShoppingCart } from "lucide-react";

export default function Settings() {
  const [searchParams] = useSearchParams();
  const [providers, setProviders] = useState([]);
  const [provider, setProvider] = useState("anthropic");
  const [model, setModel] = useState("claude-sonnet-4-5-20250929");
  const [apiKey, setApiKey] = useState("");
  const [show, setShow] = useState(false);
  const [trial, setTrial] = useState(null);
  
  // Privacy Setting State
  const [deleteAfterParse, setDeleteAfterParse] = useState(false);
  const [billingLoading, setBillingLoading] = useState(false);

  useEffect(() => {
    // Check if coming back from successful checkout session
    if (searchParams.get("session_id")) {
      toast.success("Payment completed! Your credits have been loaded.");
    }
    
    // Load metadata and API keys
    Recruit.providers().then(d => setProviders(d.providers)).catch(() => {});
    Recruit.trialStatus().then(setTrial).catch(() => {});
    Recruit.getPrivacy().then(p => setDeleteAfterParse(p.delete_resume_after_parse)).catch(() => {});
    
    const s = getSettings();
    setProvider(s.provider); setModel(s.model); setApiKey(s.apiKey);
  }, [searchParams]);

  useEffect(() => {
    if (!provider) return;
    Recruit.listModels(provider, apiKey)
      .then(res => {
        if (res && res.models) {
          setProviders(prev => prev.map(p => {
            if (p.id === provider) {
              return { ...p, models: res.models };
            }
            return p;
          }));
          
          const s = getSettings();
          if (res.models.length && (!model || !res.models.includes(model))) {
            if (res.models.includes(s.model)) {
              setModel(s.model);
            } else {
              setModel(res.models[0]);
            }
          }
        }
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [provider, apiKey]);

  const current = providers.find(p => p.id === provider);

  const save = () => {
    saveSettings({ provider, model, apiKey: apiKey.trim() });
    toast.success("Settings saved. Your key stays in this browser only.");
  };

  const clear = () => {
    setApiKey("");
    saveSettings({ provider, model, apiKey: "" });
    toast.success("Key removed. You are using your platform credits.");
  };

  // Toggle privacy compliance setting
  const handleTogglePrivacy = async (val) => {
    try {
      setDeleteAfterParse(val);
      await Recruit.updatePrivacy(val);
      toast.success(
        val 
          ? "Privacy compliance activated. Resumes will be deleted immediately after parsing." 
          : "Standard mode active. Raw resumes will be saved to your private folder."
      );
    } catch {
      toast.error("Failed to update privacy settings.");
    }
  };

  // Trigger Stripe Checkout
  const handleBuyCredits = async () => {
    setBillingLoading(true);
    try {
      const data = await Recruit.createCheckoutSession();
      if (data && data.url) {
        window.location.href = data.url;
      } else {
        throw new Error("Invalid session url");
      }
    } catch (err) {
      toast.error("Billing gateway failed. Please try again.");
    } finally {
      setBillingLoading(false);
    }
  };

  // Claim Complimentary Beta Testing Credits ($0.00)
  const handleClaimBetaCredits = async () => {
    setBillingLoading(true);
    try {
      const res = await Recruit.claimBetaCredits();
      toast.success(res.message || "Claimed +5 Beta Evaluation Credits ($0.00)!");
      const status = await Recruit.trialStatus();
      setTrial(status);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to claim beta credits.");
    } finally {
      setBillingLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-canvas" data-testid="settings-page">
      <Nav />
      <main className="max-w-[900px] mx-auto px-6 lg:px-8 py-12">
        <div className="mb-10">
          <div className="eyebrow mb-3">Settings</div>
          <h1 className="display-1 text-4xl lg:text-5xl text-ink">Your Hiring Control Center</h1>
          <p className="text-sub mt-3 max-w-2xl leading-relaxed">
            Manage your AI extraction engine configurations, buy usage credits, configure raw document PII data retention privacy rules, or bring your own LLM API key.
          </p>
        </div>

        {/* Trial / Credits Status Banner */}
        {trial && (
          <div className={`card p-6 mb-8 flex items-center gap-4 ${
            hasKey() ? "bg-white" : trial.remaining > 0 ? "bg-white" : "bg-danger/5 border-danger"
          }`}>
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 border ${
              hasKey() ? "bg-haze border-line" : trial.remaining > 0 ? "bg-accentSoft border-accentSoft text-accent" : "bg-danger/10 text-danger border-danger/10"
            }`}>
              {hasKey() ? <Check size={18} className="text-ink" /> : <Sparkles size={18} />}
            </div>
            <div className="flex-1 text-xs">
              {hasKey() ? (
                <>
                  <div className="font-bold uppercase tracking-wider text-ink">Bring-Your-Own-Key Mode Active</div>
                  <div className="text-sub mt-1">Unlimited runs. Your key remains stored in this browser only.</div>
                </>
              ) : trial.remaining > 0 ? (
                <>
                  <div className="font-bold uppercase tracking-wider text-accent" data-testid="trial-banner">
                    Free Trial — {trial.remaining} Credits Remaining
                  </div>
                  <div className="text-sub mt-1">Powered by Claude 3.5 Haiku & Gemini 2.5 Flash. Plug in your own API key to bypass credits gating.</div>
                </>
              ) : (
                <>
                  <div className="font-bold uppercase tracking-wider text-danger" data-testid="trial-exhausted">
                    0 Credits Remaining
                  </div>
                  <div className="text-sub mt-1">Credit balance exhausted. Buy credits via Stripe below or supply your own API key.</div>
                </>
              )}
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-start">
          {/* Main Provider and Key Settings */}
          <div className="lg:col-span-2 card p-8 bg-white space-y-8">
            <div>
              <h2 className="text-lg font-semibold text-ink mb-1">AI Provider</h2>
              <p className="text-xs text-soft mb-4">Select the LLM platform vendor to use with your custom API key.</p>
              <div className="grid grid-cols-3 gap-2">
                {providers.map(p => (
                  <button
                    key={p.id} data-testid={`provider-${p.id}`}
                    onClick={() => { setProvider(p.id); setModel(p.models[0]); }}
                    className={`px-3 py-2.5 border text-xs font-medium uppercase tracking-wider transition-all rounded-full ${
                      provider === p.id 
                        ? "bg-ink text-white border-ink" 
                        : "bg-white text-ink border-line hover:bg-haze"
                    }`}
                  >
                    {p.label}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <h2 className="text-lg font-semibold text-ink mb-1">Model Selection</h2>
              <p className="text-xs text-soft mb-3">Choose from live models fetched directly from {current?.label || provider} API, or enter a custom model string.</p>
              <div className="space-y-2">
                <select
                  data-testid="model-select" value={(current?.models || []).includes(model) ? model : "custom"}
                  onChange={(e) => {
                    if (e.target.value !== "custom") {
                      setModel(e.target.value);
                    } else {
                      setModel("");
                    }
                  }}
                  className="w-full border border-line bg-white py-3 px-4 focus:border-accent focus:ring-0 font-mono text-xs rounded-xl cursor-pointer"
                >
                  {(current?.models || []).map(m => (<option key={m} value={m}>{m}</option>))}
                  <option value="custom">⚙️ Custom / Unlisted Model...</option>
                </select>
                {(!(current?.models || []).includes(model) || model === "") && (
                  <input
                    type="text"
                    data-testid="custom-model-input"
                    value={model}
                    onChange={(e) => setModel(e.target.value.trim())}
                    placeholder="e.g. gpt-4.5-preview or claude-3-7-sonnet-latest"
                    className="input-apple w-full font-mono text-xs"
                  />
                )}
              </div>
            </div>

            <div>
              <h2 className="text-lg font-semibold text-ink mb-1">Custom API Key</h2>
              <p className="text-xs text-soft mb-3">Saved locally in this browser. Keys are sent straight to models via request headers.</p>
              <div className="flex items-stretch gap-2">
                <input
                  data-testid="api-key-input" type={show ? "text" : "password"} value={apiKey}
                  onChange={(e) => setApiKey(e.target.value)}
                  placeholder={provider === "openai" ? "sk-..." : provider === "anthropic" ? "sk-ant-..." : "AIza..."}
                  className="input-apple flex-1"
                />
                <button 
                  onClick={() => setShow(s => !s)} 
                  className="px-4 border border-line hover:bg-haze rounded-xl transition-all" 
                  data-testid="toggle-key"
                >
                  {show ? <EyeOff size={14} /> : <Eye size={14} />}
                </button>
              </div>
              <div className="flex items-center gap-1.5 mt-3 font-sans text-[10px] text-soft">
                <Info size={10} /> Leave empty to use default credits trial balance.
              </div>
            </div>

            <div className="flex justify-between items-center pt-6 border-t hairline">
              {apiKey && (
                <button onClick={clear} className="font-medium text-xs text-danger hover:underline" data-testid="clear-key-btn">
                  Remove Key
                </button>
              )}
              <button 
                data-testid="save-settings-btn" 
                onClick={save} 
                className="btn-primary text-sm ml-auto"
              >
                Save Settings
              </button>
            </div>
          </div>

          {/* Right Column: Pricing & Privacy Control */}
          <div className="space-y-8">
            {/* Early Access Beta Program & Evaluation Grants */}
            <div className="card p-8 bg-white">
              <div className="flex items-center gap-2 mb-4">
                <ShoppingCart className="h-5 w-5 text-accent" />
                <h2 className="text-base font-semibold text-ink">Early Access Beta Program</h2>
              </div>
              <p className="text-xs text-sub leading-relaxed mb-6 font-sans">
                Recruit.IQ is currently in <strong>Early Access Beta</strong>. You can claim complimentary evaluation credits (<strong>$0.00</strong>) to test our AI candidate analysis engine and experience the credit purchasing workflow.
              </p>
              <button
                onClick={handleClaimBetaCredits}
                disabled={billingLoading}
                className="w-full flex items-center justify-center py-3 border border-accent bg-accent text-white hover:bg-accent/90 rounded-full text-xs font-medium uppercase tracking-wider transition-all disabled:opacity-50 shadow-soft"
              >
                {billingLoading ? "Allocating Credits..." : "Claim Beta Credits (+5 Credits — $0.00)"}
              </button>
              <div className="text-[10px] text-soft mt-3 leading-normal font-sans text-center">
                Need extra evaluation credits or have product feedback? <a href="mailto:feedback@recruitiq.ai" className="text-accent underline font-medium">Send Beta Feedback</a>
              </div>
            </div>

            {/* PII Privacy Compliance Toggle */}
            <div className="card p-8 bg-white">
              <div className="flex items-center gap-2 mb-4">
                <Shield className="h-5 w-5 text-ink" />
                <h2 className="text-base font-semibold text-ink">PII Data Privacy</h2>
              </div>
              <p className="text-xs text-sub leading-relaxed mb-6 font-sans">
                GDPR/CCPA Compliance: Choose whether raw PDF resume files are stored on our servers or deleted immediately after parsing.
              </p>
              <div className="flex items-center justify-between border-t hairline pt-4">
                <span className="text-xs font-sans text-ink">Compliance Mode</span>
                <button
                  onClick={() => handleTogglePrivacy(!deleteAfterParse)}
                  className={`w-12 h-6 border border-line rounded-full transition-all flex items-center ${
                    deleteAfterParse ? "bg-accent" : "bg-haze"
                  }`}
                >
                  <div className={`w-4 h-4 rounded-full transition-all ${
                    deleteAfterParse ? "translate-x-6 bg-white" : "translate-x-1 bg-white border border-line shadow-soft"
                  }`}></div>
                </button>
              </div>
              <div className="text-[10px] text-soft mt-3 leading-normal font-sans">
                {deleteAfterParse 
                  ? "Compliance active: Resumes parsed in-memory, PDFs deleted from servers instantly."
                  : "Standard active: PDFs saved in your private folder for browser previews."}
              </div>
            </div>
          </div>
        </div>

        {/* Get keys links */}
        <div className="mt-12">
          <h3 className="text-base font-serif font-light text-ink mb-4">External Key Sources</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {[
              ["OpenAI Portal", "https://platform.openai.com/api-keys", "platform.openai.com"],
              ["Anthropic Console", "https://console.anthropic.com/settings/keys", "console.anthropic.com"],
              ["Google AI Studio", "https://aistudio.google.com/app/apikey", "aistudio.google.com"],
            ].map(([name, url, desc]) => (
              <a 
                key={name} href={url} target="_blank" rel="noreferrer" 
                className="card p-5 bg-white hover:bg-haze flex items-center justify-between transition-all"
              >
                <div>
                  <div className="text-xs font-bold text-ink font-sans">{name}</div>
                  <div className="text-[10px] font-mono text-soft mt-1">{desc}</div>
                </div>
                <ExternalLink size={14} className="text-soft" />
              </a>
            ))}
          </div>
        </div>
      </main>
    </div>
  );
}
